import React from 'react';
import { MessageCircle, Menu, Settings, LogOut, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Sheet,
    SheetContent,
    SheetTrigger,
} from "@/components/ui/sheet";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslations } from '@/lib/translations';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_wonderchat-mobile/artifacts/9pn3gi8c_ChatGPT%20Image%207%20feb%202026%2C%2019_58_01.png";

export const Header = ({ onOpenChat, onOpenHistory, isAdmin, onOpenAdmin, onLogout }) => {
    const t = useTranslations();
    
    const navItems = [
        { label: t.home, href: '#home' },
        { label: t.features, href: '#features' },
    ];

    return (
        <header className="fixed top-0 left-0 right-0 z-50 glass-dark border-b border-border/50">
            <div className="container mx-auto px-4 h-14 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <img 
                        src={LOGO_URL} 
                        alt="Logo" 
                        className="h-10 w-10 object-contain"
                    />
                </div>

                {/* Desktop Navigation */}
                <nav className="hidden md:flex items-center gap-6">
                    {navItems.map((item) => (
                        <a
                            key={item.label}
                            href={item.href}
                            className="text-sm text-foreground/80 hover:text-primary transition-colors duration-300"
                        >
                            {item.label}
                        </a>
                    ))}
                </nav>

                {/* Right side buttons */}
                <div className="flex items-center gap-2">
                    {/* Settings Menu */}
                    <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={onOpenHistory}
                        className="text-foreground"
                        title="Chat History"
                        data-testid="header-history-btn"
                    >
                        <History className="w-5 h-5" />
                    </Button>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-foreground" data-testid="settings-menu-btn">
                                <Settings className="w-5 h-5" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-card border-border">
                            {isAdmin && (
                                <>
                                    <DropdownMenuItem 
                                        onClick={onOpenAdmin}
                                        className="cursor-pointer text-foreground"
                                        data-testid="admin-panel-menu-item"
                                    >
                                        <Settings className="w-4 h-4 mr-2" />
                                        Admin Panel
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                </>
                            )}
                            <DropdownMenuItem 
                                onClick={onLogout}
                                className="cursor-pointer text-destructive"
                                data-testid="logout-menu-item"
                            >
                                <LogOut className="w-4 h-4 mr-2" />
                                Logout
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Mobile Menu */}
                    <Sheet>
                        <SheetTrigger asChild>
                            <Button variant="ghost" size="icon" className="md:hidden text-foreground">
                                <Menu className="w-5 h-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="bg-card border-border">
                            <div className="flex flex-col gap-6 mt-8">
                                <div className="flex items-center gap-3 mb-4">
                                    <img 
                                        src={LOGO_URL} 
                                        alt="Logo" 
                                        className="h-14 w-14 object-contain"
                                    />
                                </div>
                                
                                {navItems.map((item) => (
                                    <a
                                        key={item.label}
                                        href={item.href}
                                        className="text-lg text-foreground hover:text-primary transition-colors py-2 border-b border-border/50"
                                    >
                                        {item.label}
                                    </a>
                                ))}

                                {isAdmin && (
                                    <button
                                        onClick={onOpenAdmin}
                                        className="text-lg text-foreground hover:text-primary transition-colors py-2 border-b border-border/50 text-left"
                                    >
                                        Admin Panel
                                    </button>
                                )}
                                
                                <Button 
                                    onClick={onLogout}
                                    variant="outline"
                                    className="border-destructive text-destructive hover:bg-destructive/10"
                                >
                                    <LogOut className="w-4 h-4 mr-2" />
                                    Logout
                                </Button>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
};

export default Header;
